/**
 * Backend `extensions.code` taxonomy + the per-operation error-handling mode.
 * Keep in sync with `backend/core/graphql/error_reporting.py`.
 */

import type { GraphQLFormattedError } from 'graphql';

export type GraphqlErrorCode =
  | 'AUTH_REQUIRED'
  | 'BAD_USER_INPUT'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'FORBIDDEN'
  | 'GRAPHQL_VALIDATION_FAILED'
  | 'GRAPHQL_PARSE_FAILED'
  | 'INTERNAL_SERVER_ERROR';

/** Value of `operation.context.errorHandling`. Default: `notify` for queries, `silent` for mutations. */
export type ErrorHandlingMode = 'notify' | 'silent';

const KNOWN_CODES: ReadonlySet<string> = new Set<GraphqlErrorCode>([
  'AUTH_REQUIRED',
  'BAD_USER_INPUT',
  'NOT_FOUND',
  'CONFLICT',
  'FORBIDDEN',
  'GRAPHQL_VALIDATION_FAILED',
  'GRAPHQL_PARSE_FAILED',
  'INTERNAL_SERVER_ERROR',
]);

export interface GraphqlErrorLike {
  message?: string;
  locations?: unknown;
  path?: unknown;
  extensions?: Record<string, unknown> | null;
}

interface NetworkErrorWithResult {
  result?: { errors?: GraphqlErrorLike[] };
}

/** First error, whether on `graphQLErrors` or the 400-case `networkError.result.errors`. */
export const firstErrorFrom = (
  graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined,
  networkError: Error | null | undefined
): GraphqlErrorLike | undefined => {
  return graphqlErrorsFrom(graphQLErrors, networkError)[0];
};

/** All errors, normalising the 400-with-GraphQL-errors shape. Duck-type lives here, not at call sites. */
export const graphqlErrorsFrom = (
  graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined,
  networkError: Error | null | undefined
): ReadonlyArray<GraphqlErrorLike> => {
  if (graphQLErrors?.length) return graphQLErrors;
  const result = (networkError as NetworkErrorWithResult | null | undefined)?.result;
  return Array.isArray(result?.errors) ? result.errors : [];
};

/** Raw `extensions.code`, ungated. Use for telemetry so new BE codes stay searchable. */
export const getGraphqlErrorRawCode = (
  graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined,
  networkError: Error | null | undefined
): string | undefined => {
  const code = firstErrorFrom(graphQLErrors, networkError)?.extensions?.code;
  return typeof code === 'string' ? code : undefined;
};

/** First error's code, narrowed to a known one. Drives cross-cutting UX (snackbar/redirect). */
export const getGraphqlErrorCode = (
  graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined,
  networkError: Error | null | undefined
): GraphqlErrorCode | undefined => {
  const code = getGraphqlErrorRawCode(graphQLErrors, networkError);
  return code && KNOWN_CODES.has(code) ? (code as GraphqlErrorCode) : undefined;
};

/** `extensions.subcode` — domain discriminator for component-local dispatch. Branch on this, not the message. */
export const getGraphqlErrorSubcode = (
  graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined,
  networkError: Error | null | undefined
): string | undefined => {
  const sub = firstErrorFrom(graphQLErrors, networkError)?.extensions?.subcode;
  return typeof sub === 'string' ? sub : undefined;
};

interface ApolloErrorLike {
  graphQLErrors?: ReadonlyArray<GraphQLFormattedError>;
  networkError?: Error | null;
}

/**
 * Duck-types `graphQLErrors`/`networkError` off a caught value. Use instead of
 * `instanceof ApolloError`, which breaks if two `@apollo/client` copies get bundled.
 */
export const extractApolloError = (
  error: unknown
): { graphQLErrors: ReadonlyArray<GraphQLFormattedError>; networkError: Error | null } | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as ApolloErrorLike;
  if (!('graphQLErrors' in e) && !('networkError' in e)) return undefined;
  return { graphQLErrors: e.graphQLErrors ?? [], networkError: e.networkError ?? null };
};
